// Generated from D:/PJ/ieum/ieum-auto/apps/realtorauto-web/components/realtorauto/RealtorAutoDocumentLayerEditorModal.tsx
// Source SHA-256: e37d404e00a1364f3e129a469416dad67ddeed3a5845f43e5e9c45086f8bed56
// 원본 RealtorAuto 편집기 동기화 파일입니다. 직접 수정하지 말고 syncRealtorAutoOriginalTemplateEditor.cjs를 실행하세요.
'use client';

import { Loader2, Save, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
const INPUT_CLASS = 'mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-950 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100 disabled:text-slate-500';
const FIELD_LABEL_CLASS = 'block text-xs font-semibold text-slate-600';
const CONTRACT_TEMPLATE_MIN_AUTO_FIT_FONT_SIZE_PX = 7;
const CONTRACT_TEMPLATE_MAX_AUTO_FIT_FONT_SIZE_PX = 13;
export function escapeTemplateRichText(value) {
  return value.replace(/&/gu, '&amp;').replace(/</gu, '&lt;').replace(/>/gu, '&gt;').replace(/"/gu, '&quot;').replace(/'/gu, '&#39;');
}
function plainTemplateRichText(html, options) {
  const source = String(html ?? '');
  const normalized = options?.preserveLineBreaks ? source.replace(/<div><br><\/div>/giu, '\n').replace(/<p><br><\/p>/giu, '\n').replace(/<br\s*\/?>/giu, '\n').replace(/<\/(?:div|p)>/giu, '\n').replace(/<(?:div|p)(?:\s[^>]*)?>/giu, '') : source.replace(/<br\s*\/?>/giu, '');
  const text = normalized.replace(/<[^>]+>/gu, '').replace(/&nbsp;/giu, ' ').replace(/&amp;/giu, '&').replace(/&lt;/giu, '<').replace(/&gt;/giu, '>').replace(/&quot;/giu, '"').replace(/&#39;/giu, "'");
  return options?.preserveLineBreaks ? text.replace(/\n{3,}/gu, '\n\n').replace(/^\n|\n$/gu, '') : text;
}
export function normalizeTemplateRichHtmlFromElement(element, options) {
  const rawHtml = element.innerHTML;
  const text = (options?.preserveLineBreaks ? plainTemplateRichText(rawHtml, {
    preserveLineBreaks: true
  }) : element.textContent ?? '').replace(/\u00a0/gu, ' ');
  if (!text) return {
    html: '',
    text: ''
  };
  return {
    html: options?.preserveLineBreaks ? rawHtml : rawHtml.replace(/<div><br><\/div>/giu, '').replace(/<br\s*\/?>/giu, ''),
    text
  };
}
function cssPxNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return undefined;
  const match = value.trim().match(/^(-?\d+(?:\.\d+)?)px$/u);
  if (!match) return undefined;
  const numeric = Number(match[1]);
  return Number.isFinite(numeric) ? numeric : undefined;
}
function contractAutoFieldIsAddressField({
  multiline,
  signatureAddress,
  fieldKey,
  placeholder,
  title
}) {
  if (signatureAddress) return true;
  if (!multiline) return false;
  return /(address)/iu.test(String(fieldKey ?? '')) || /(주소|소재지)/u.test(`${placeholder} ${title}`);
}
function measuredSingleLineTextWidthPx(element, text, fontSizePx) {
  const ownerDocument = element.ownerDocument;
  const canvas = ownerDocument.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return Number.POSITIVE_INFINITY;
  const computed = ownerDocument.defaultView?.getComputedStyle(element);
  const fontStyle = computed?.fontStyle || 'normal';
  const fontWeight = computed?.fontWeight || '400';
  const fontFamily = computed?.fontFamily || 'Dotum, Gulim, Arial, sans-serif';
  const letterSpacingPx = cssPxNumber(computed?.letterSpacing) ?? 0;
  context.font = `${fontStyle} ${fontWeight} ${fontSizePx}px ${fontFamily}`;
  return context.measureText(text).width + Math.max(0, text.length - 1) * letterSpacingPx;
}
function contractAddressTextNeedsTwoLines(element, text, minFontSizePx) {
  const clean = String(text ?? '').replace(/\s+/gu, ' ').trim();
  if (!clean) return false;
  if (String(text ?? '').includes('\n')) return true;
  const computed = element.ownerDocument.defaultView?.getComputedStyle(element);
  const horizontalPadding = (cssPxNumber(computed?.paddingLeft) ?? 0) + (cssPxNumber(computed?.paddingRight) ?? 0);
  const availableWidth = Math.max(1, element.clientWidth - horizontalPadding - 1);
  const minWidth = measuredSingleLineTextWidthPx(element, clean, minFontSizePx);
  return minWidth > availableWidth;
}
function useContractAddressSingleLineFirstMultiline(inputRef, textareaRef, text, enabled, style) {
  const [needsTwoLines, setNeedsTwoLines] = useState(false);
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setNeedsTwoLines(false);
      return undefined;
    }
    let animationFrame = 0;
    let resizeObserver = null;
    const update = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const element = inputRef.current ?? textareaRef.current;
        if (!element) return;
        const next = contractAddressTextNeedsTwoLines(element, text, CONTRACT_TEMPLATE_MIN_AUTO_FIT_FONT_SIZE_PX);
        setNeedsTwoLines(previous => previous === next ? previous : next);
      });
    };
    const element = inputRef.current ?? textareaRef.current;
    if (element && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(element);
    }
    update();
    window.addEventListener('resize', update);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [enabled, inputRef, needsTwoLines, style?.fontSize, style?.letterSpacing, style?.paddingLeft, style?.paddingRight, style?.width, textareaRef, text]);
  return enabled && needsTwoLines;
}
export function useAutoFitTextElement(ref, text, style, options = {}) {
  const enabled = options.enabled ?? true;
  const multiline = options.multiline ?? false;
  const minFontSizePx = options.minFontSizePx ?? 5;
  const maxFontSizePx = options.maxFontSizePx;
  const maxLines = options.maxLines;
  const styleFontSizePx = cssPxNumber(style?.fontSize);
  const styleLineHeightPx = cssPxNumber(style?.lineHeight);
  useEffect(() => {
    const element = ref.current;
    if (!enabled || !element || typeof window === 'undefined') return undefined;
    let animationFrame = 0;
    const applyFont = (fontSizePx, baseFontPx, baseLineHeightPx) => {
      element.style.fontSize = `${fontSizePx}px`;
      if (baseLineHeightPx) {
        const ratio = baseFontPx > 0 ? fontSizePx / baseFontPx : 1;
        element.style.lineHeight = `${Math.max(fontSizePx + 1, baseLineHeightPx * ratio)}px`;
      }
    };
    const fit = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const computed = window.getComputedStyle(element);
        const baseFontPx = Math.min(maxFontSizePx ?? Number.POSITIVE_INFINITY, styleFontSizePx ?? cssPxNumber(computed.fontSize) ?? 10);
        const baseLineHeightPx = styleLineHeightPx ?? cssPxNumber(computed.lineHeight);
        applyFont(baseFontPx, baseFontPx, baseLineHeightPx);
        if (!String(text ?? '').trim()) return;
        const availableWidth = element.clientWidth;
        const availableHeight = element.clientHeight;
        if (availableWidth <= 0 || multiline && availableHeight <= 0) return;
        const minPx = Math.max(3.5, Math.min(baseFontPx, minFontSizePx));
        const maxPx = Math.max(minPx, maxFontSizePx ?? baseFontPx);
        let nextFontPx = maxPx;
        applyFont(nextFontPx, baseFontPx, baseLineHeightPx);
        const hasOverflow = () => {
          const currentComputed = window.getComputedStyle(element);
          const lineHeightPx = cssPxNumber(currentComputed.lineHeight) ?? cssPxNumber(element.style.lineHeight) ?? nextFontPx + 1;
          const verticalPaddingPx = (cssPxNumber(currentComputed.paddingTop) ?? 0) + (cssPxNumber(currentComputed.paddingBottom) ?? 0);
          const heightLimit = multiline && maxLines && maxLines > 0 ? Math.min(availableHeight, lineHeightPx * maxLines + verticalPaddingPx + 1) : availableHeight;
          const widthOverflow = element.scrollWidth > availableWidth + 1;
          const fullHeightOverflow = element.scrollHeight > availableHeight + 1;
          const maxLineOverflow = multiline && element.scrollHeight > heightLimit + 1;
          const heightOverflow = multiline && (fullHeightOverflow || maxLineOverflow);
          return widthOverflow || heightOverflow;
        };
        for (let i = 0; i < 80 && nextFontPx > minPx && hasOverflow(); i += 1) {
          nextFontPx = Math.max(minPx, nextFontPx - 0.5);
          applyFont(nextFontPx, baseFontPx, baseLineHeightPx);
        }
      });
    };
    fit();
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    resizeObserver?.observe(element);
    window.addEventListener('resize', fit);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [enabled, maxFontSizePx, maxLines, multiline, minFontSizePx, ref, styleFontSizePx, styleLineHeightPx, style?.height, style?.paddingBottom, style?.paddingLeft, style?.paddingRight, style?.paddingTop, style?.width, text]);
}
export function TemplateInlineTextEditor({
  value,
  onChange,
  htmlValue,
  onHtmlChange,
  disabled,
  className,
  multiline = false,
  richMultiline = false,
  placeholder,
  style,
  onFocus,
  onSelectionCapture,
  fieldKey
}) {
  const richRef = useRef(null);
  const textareaRef = useRef(null);
  const richComposingRef = useRef(false);
  useAutoFitTextElement(textareaRef, value, style, {
    enabled: multiline && !richMultiline,
    multiline: true,
    minFontSizePx: CONTRACT_TEMPLATE_MIN_AUTO_FIT_FONT_SIZE_PX
  });
  useAutoFitTextElement(richRef, value, style, {
    enabled: !(multiline && !richMultiline),
    multiline: multiline || richMultiline,
    minFontSizePx: CONTRACT_TEMPLATE_MIN_AUTO_FIT_FONT_SIZE_PX
  });
  const preserveLineBreaks = true;
  const richHtml = htmlValue && htmlValue.length > 0 && plainTemplateRichText(htmlValue, {
    preserveLineBreaks
  }) === value ? htmlValue : escapeTemplateRichText(value);
  useEffect(() => {
    const element = richRef.current;
    if (!element || document.activeElement === element || richComposingRef.current) return;
    if (element.innerHTML !== richHtml) element.innerHTML = richHtml;
  }, [richHtml]);
  const commitRichTextChange = element => {
    const next = normalizeTemplateRichHtmlFromElement(element, {
      preserveLineBreaks
    });
    if (onHtmlChange) {
      onHtmlChange(next.html, next.text);
    } else {
      onChange(next.text);
    }
  };
  if (multiline && !richMultiline) {
    return <textarea ref={textareaRef} value={value} onChange={event => onChange(event.target.value)} onFocus={onFocus} disabled={disabled} className={`${className} resize-none border-0 bg-transparent outline-none placeholder:text-slate-300 focus:bg-blue-50 print:bg-transparent`} placeholder={placeholder} style={style} data-template-edit-field={fieldKey} />;
  }
  return <div ref={richRef} contentEditable={!disabled} suppressContentEditableWarning role="textbox" aria-label={placeholder || fieldKey || '텍스트'} onInput={event => {
    const inputEvent = event.nativeEvent;
    if (richComposingRef.current || inputEvent.isComposing) return;
    commitRichTextChange(event.currentTarget);
  }} onCompositionStart={() => {
    richComposingRef.current = true;
  }} onCompositionEnd={event => {
    richComposingRef.current = false;
    commitRichTextChange(event.currentTarget);
  }} onFocus={() => {
    onFocus?.();
    onSelectionCapture?.();
  }} onSelect={onSelectionCapture} onMouseUp={onSelectionCapture} onKeyUp={onSelectionCapture} onPaste={event => {
    event.preventDefault();
    const text = event.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }} className={`${className} whitespace-pre-wrap border-0 ${richMultiline ? '' : 'bg-transparent'} outline-none empty:before:text-slate-300 empty:before:content-[attr(data-placeholder)] focus:bg-blue-50 print:bg-transparent`} style={style} data-placeholder={placeholder ?? ''} data-template-edit-field={fieldKey} data-template-rich-field={fieldKey} />;
}
function isContractSignatureAutoFieldKey(fieldKey) {
  return /^auto-(?:lessorOrSeller|lesseeOrBuyer|primaryBroker|coBroker)/u.test(String(fieldKey ?? ''));
}
export function AutomationLockedField({
  value,
  placeholder,
  className,
  title,
  showHint = false,
  plainReadOnly = false,
  multiline = false,
  maxLines,
  style,
  onFocus,
  onChange,
  fieldKey
}) {
  const display = String(value ?? '');
  const editable = Boolean(onChange);
  const fieldEditable = editable && !plainReadOnly;
  const visiblePlaceholder = showHint ? placeholder : '';
  const inputRef = useRef(null);
  const textareaRef = useRef(null);
  const isSignatureAutoFieldByKey = isContractSignatureAutoFieldKey(fieldKey);
  const isSignatureAutoValue = className.includes('contract-signature-auto-value') || isSignatureAutoFieldByKey;
  const isSignatureAddressAutoValue = isSignatureAutoValue && (/(address)/iu.test(String(fieldKey ?? '')) || /(주소|소재지)/u.test(`${placeholder} ${title}`));
  const contractAddressField = contractAutoFieldIsAddressField({
    multiline,
    signatureAddress: isSignatureAddressAutoValue,
    fieldKey,
    placeholder,
    title
  });
  const contractAddressTwoLineMode = useContractAddressSingleLineFirstMultiline(inputRef, textareaRef, display, contractAddressField, style);
  const effectiveMultiline = contractAddressField ? contractAddressTwoLineMode : multiline;
  const effectiveMaxLines = maxLines ?? (contractAddressField ? 2 : undefined);
  const signatureAutoFitMaxFontSizePx = isSignatureAutoValue ? CONTRACT_TEMPLATE_MAX_AUTO_FIT_FONT_SIZE_PX : undefined;
  useAutoFitTextElement(textareaRef, display, style, {
    enabled: effectiveMultiline,
    multiline: true,
    minFontSizePx: CONTRACT_TEMPLATE_MIN_AUTO_FIT_FONT_SIZE_PX,
    maxFontSizePx: signatureAutoFitMaxFontSizePx,
    maxLines: effectiveMaxLines
  });
  useAutoFitTextElement(inputRef, display, style, {
    enabled: !effectiveMultiline,
    multiline: false,
    minFontSizePx: CONTRACT_TEMPLATE_MIN_AUTO_FIT_FONT_SIZE_PX,
    maxFontSizePx: signatureAutoFitMaxFontSizePx
  });
  const hasPlaceholderHint = display.length === 0 && visiblePlaceholder.trim().length > 0;
  const placeholderHintClass = hasPlaceholderHint ? 'bg-transparent text-slate-500 placeholder:font-semibold placeholder:text-slate-500' : 'bg-transparent text-slate-950 placeholder:text-slate-400/70';
  const fieldStyle = hasPlaceholderHint ? {
    ...style,
    color: '#64748b'
  } : style;
  const signatureAutoClass = isSignatureAutoFieldByKey && !className.includes('contract-signature-auto-value') ? ' contract-signature-auto-value' : '';
  const commonClass = `${className}${signatureAutoClass} locked-auto-field h-full w-full ${fieldEditable ? 'cursor-text focus:bg-white focus:ring-1 focus:ring-inset focus:ring-blue-400' : 'cursor-default'} border-0 ${placeholderHintClass} px-1 py-0.5 outline-none ring-0 print:bg-transparent print:text-black print:ring-0`;
  if (plainReadOnly) {
    return <div className={`${commonClass} flex items-center ${effectiveMultiline ? 'whitespace-pre-wrap' : 'whitespace-nowrap'}`} style={fieldStyle} title={showHint ? `${title} · 자동입력값` : title} data-contract-signature-auto-field={isSignatureAutoValue ? 'true' : undefined} data-template-edit-field={fieldKey}>
        {display || visiblePlaceholder}
      </div>;
  }
  if (effectiveMultiline) {
    return <textarea ref={textareaRef} value={display} readOnly={!editable} onFocus={onFocus} onChange={event => onChange?.(event.target.value)} className={`${commonClass} resize-none`} placeholder={visiblePlaceholder} title={showHint ? `${title} · 자동입력값` : title} style={fieldStyle} data-contract-signature-auto-field={isSignatureAutoValue ? 'true' : undefined} data-template-auto-field={fieldKey} />;
  }
  return <input ref={inputRef} value={display} readOnly={!editable} onFocus={onFocus} onChange={event => onChange?.(event.target.value)} placeholder={visiblePlaceholder} className={commonClass} title={showHint ? `${title} · 자동입력값` : title} style={fieldStyle} data-contract-signature-auto-field={isSignatureAutoValue ? 'true' : undefined} data-template-auto-field={fieldKey} />;
}
export function RealtorAutoDocumentLayerEditorModal({
  open,
  documentTypeLabel,
  fileName,
  message,
  error,
  saving,
  reason,
  versions,
  children,
  onReasonChange,
  onClose,
  onSave,
  onRestore
}) {
  const [versionNameDraft, setVersionNameDraft] = useState('');
  useEffect(() => {
    if (open) setVersionNameDraft('');
  }, [documentTypeLabel, fileName, open]);
  if (!open) return null;
  return <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/40 px-4 py-6" data-document-layer-editor>
      <div className="flex max-h-[90vh] w-full max-w-6xl flex-col rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 p-4">
          <div>
            <h2 className="text-base font-semibold text-slate-950">{documentTypeLabel} 수정</h2>
            <p className="mt-1 text-xs text-slate-500">{fileName}</p>
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" title="닫기">
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {message ? <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
              {message}
            </div> : null}
          {error ? <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
              {error}
            </div> : null}

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
            <div className="space-y-3">
              {children}
              <label className={FIELD_LABEL_CLASS}>
                수정 사유
                <input value={reason} onChange={event => onReasonChange(event.target.value)} className={INPUT_CLASS} />
              </label>
            </div>

            <aside className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs font-semibold text-slate-500">복원 버전</div>
              <div className="mt-2 space-y-2">
                {versions.length >= 2 ? <select defaultValue="" onChange={event => {
                if (event.target.value) {
                  onRestore(event.target.value);
                  event.currentTarget.value = '';
                }
              }} disabled={saving} className={INPUT_CLASS}>
                    <option value="">복원할 버전 선택</option>
                    {versions.map(version => <option key={version.id} value={version.id}>
                        {version.secondaryLabel}
                      </option>)}
                  </select> : versions.length ? versions.map(version => <button key={version.id} type="button" onClick={() => onRestore(version.id)} disabled={saving} className="block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60">
                      <span className="block text-xs font-semibold text-slate-800">{version.primaryLabel}</span>
                      <span className="mt-1 block text-[11px] text-slate-500">{version.secondaryLabel}</span>
                      {version.reason ? <span className="mt-1 block truncate text-[11px] text-slate-500">{version.reason}</span> : null}
                    </button>) : <div className="rounded-lg border border-dashed border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-400">
                    저장된 버전 없음
                  </div>}
              </div>
            </aside>
          </div>
        </div>

        <div className="flex flex-wrap items-end justify-end gap-2 border-t border-slate-200 p-4">
          <label className={`${FIELD_LABEL_CLASS} mr-auto w-full max-w-sm`}>
            저장 버전명
            <input value={versionNameDraft} onChange={event => setVersionNameDraft(event.target.value)} className={INPUT_CLASS} placeholder="예: 특약 문구 수정" disabled={saving} data-document-text-layer-version-name />
          </label>
          <button type="button" onClick={onClose} className="inline-flex h-10 items-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100">
            닫기
          </button>
          <button type="button" onClick={() => onSave(versionNameDraft.trim())} disabled={saving} className="inline-flex h-10 items-center gap-2 rounded-lg bg-blue-700 px-4 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Save className="h-4 w-4" aria-hidden />}
            텍스트 레이어 버전 저장
          </button>
        </div>
      </div>
    </div>;
}
